import React, { ReactElement, createContext, useContext, useEffect } from "react"
import { Helmet } from 'react-helmet-async'
import { useTranslation } from "react-i18next"

import { createSearchParams, Outlet, useParams, useNavigate, Link } from "../navigation"
import { useRoles } from "../user/Auth"
import { useComments } from "../comments/CommentsProvider"
import { Comment } from "../comments/Comments"
import { asUrl } from '../components/edit-table'
import { IconEdit, IconExternalLink } from '../components/icons'
import { competitor_member } from '../data'
import { useLocationHistory } from "../navigation/Location"
import DeleteButton from "../components/DeleteButton"
import { LinkedTabCard } from "../components/TabCard"
import { FocusedBrowser } from "../documents/FocusedBrowser"
import { BreadCrumbs } from "../components/BreadCrumbs"
import { emptyStringAsUndefined, truncate } from "../utils/strings"
import { useExternal } from "./"
import { competitorFamilyUrl, competitorMemberUrl } from "./utils"
import { CompetitorFamily, CompetitorMember } from "./external"
import { useAgents } from "../agents/AgentsProvider"
import { extractAgents, nameOf } from "../agents/utils"
import { useTasks } from "../tasks/TasksProvider"
import Task from "../tasks/Task"
import { deriveDate } from "../tasks/utils"
import KanjiTip from "../components/KanjiTip"
import { ExternalMemberCosts } from "../costs/CostsTable"
import _ from "lodash"

// Member + family context
const CompetitorMemberContext = createContext<{ member: CompetitorMember; family: CompetitorFamily } | null>(null)
function useCompetitorMember() { return useContext(CompetitorMemberContext) }

export function FamilyMemberIndex() {
    const { t } = useTranslation()
    const { hasDocuments, hasCosts } = useRoles()

    const { internalReference } = useParams()
    const navigate = useNavigate()
    const { getLastMatchingPath } = useLocationHistory()

    const { familyById, memberByReference } = useExternal()
    const member = memberByReference[internalReference]
    const family = familyById[member?.competitorFamilyId]

    useEffect(() => {
        const timer = setTimeout(() => {
            if (member === undefined)
                navigate("/external/portfolio")
        }, 1500)
        return () => clearTimeout(timer)
    }, [member, navigate])

    if (!member || !family) return null

    const familyName = emptyStringAsUndefined(family.familyName)

    return <>
        {/* @ts-ignore */}
        <Helmet>
            <title>{t('competitor-member')} {member.internalReference} | Patent Cockpit</title>
        </Helmet>

        <BreadCrumbs parts={[
            {
                className: 'max-sm:hidden',
                label: t('external'),
                to: (getLastMatchingPath("/external/portfolio?")?.path ?? "..") + '#' + encodeURIComponent(family.internalReference)
            },
            <Link key={family.internalReference} to={competitorFamilyUrl(family)} className="modern-h2">
                <span>{family.internalReference}</span>  {familyName && <span className="hidden lg:inline ml-3 text-pcx-500">{truncate(familyName)}</span>}
            </Link>,
            { to: competitorMemberUrl(member), label: member.internalReference }
        ]} />
        <div className="overflow-auto grow px-2 sm:px-4">
            <div className="w-full md:w-fit">
                <div className="lg:min-w-5xl xl:min-w-6xl pb-4">
                    <CompetitorMemberContext.Provider value={{ member, family }}>
                        <LinkedTabCard links={[
                            { to: ".", label: t('overview') },
                            { to: "claims", label: t('claims') },
                            hasDocuments && { to: "documents", label: t('documents') },
                            hasCosts && { to: "costs", label: t('costs') },
                        ]} />
                    </CompetitorMemberContext.Provider>
                </div>
            </div>
        </div>
    </>
}

export function MemberOverview() {
    const { isEditUser, hasTasks } = useRoles()
    const ctx = useCompetitorMember()

    if (!ctx) return null
    const { member, family } = ctx

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(40rem,48rem)_45ch] max-lg:divide-y-2 lg:divide-x-2 divide-pcx-200">
            <div className="max-lg:pb-4 lg:pr-4 grow">
                <MemberDetails member={member} family={family} isEditUser={isEditUser} />
            </div>
            <div className="max-lg:pt-4 lg:pl-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-6 items-start">
                    {hasTasks && <TasksOfMember member={member} />}
                    <MemberComments member={member} />
                </div>
            </div>
            <Outlet />
        </div>
    )
}

function TasksOfMember({ member }: { member: CompetitorMember }) {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()
    const { tasksLookUp } = useTasks()

    const tasks = _(tasksLookUp[competitor_member]?.[member.competitorMemberId] ?? [])
        .filter(t => !t.done)
        .map(deriveDate)
        .sortBy(t => t.date)
        .value()

    const basicParams = { entity: competitor_member, entityId: '' + member.competitorMemberId }

    return (
        <div className="flex flex-col gap-2 sm:max-w-lg">
            <div className="flex flex-row">
                <h4 className="grow">{t('tasks')}</h4>
                {isEditUser &&
                    <Link title={t('add-task')} to={`task?${createSearchParams(basicParams).toString()}`} className="text-sm btn-secondary py-0.5">
                        {t('add')}
                    </Link>}
            </div>
            {tasks.map(task => <Task key={task.taskId ?? '-1'} {...{ task, entity: competitor_member, entityId: member.competitorMemberId }} />)}
        </div>
    )
}

function MemberDetails({ member, family, isEditUser }: { member: CompetitorMember; family: CompetitorFamily; isEditUser: boolean }) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { deleteMember, agentsByMemberId: competitorAgentsByMemberId } = useExternal()
    const { agentById } = useAgents()

    const competitorMemberId = member.competitorMemberId
    const tagDisplays = _.mapValues(agentById, a => nameOf(a))
    const currentInventors = extractAgents('inventor', competitorMemberId, competitorAgentsByMemberId, agentById)
    const currentOwners = extractAgents('owner', competitorMemberId, competitorAgentsByMemberId, agentById)
    const currentApplicants = extractAgents('applicant', competitorMemberId, competitorAgentsByMemberId, agentById)

    const statusDescription = `${t(member.competitorMemberStatus)} ${t(member.ipType)} ${t('in')} ${member.countryCode}`

    return (
        <div className="flex flex-col">
            <div className="border-b-2 border-pcx-200 pb-5">
                <div className="flex flex-row gap-1 w-full">
                    <h3 className="mb-4 max-w-prose text-base font-normal text-gray-500 mr-4 grow">{member.title}</h3>
                    {isEditUser && <>
                        <Link title={t('edit')} to="edit" className="btn-primary ml-auto shrink-0 w-5 h-5 p-px"><IconEdit /></Link>
                        <DeleteButton
                            className="w-5 h-5 p-px btn-warn shrink-0"
                            deleteAction={() => deleteMember(member)
                                .then(() => navigate(competitorFamilyUrl(family)))
                            }
                        />
                    </>}
                </div>
                <div className="self-start grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-1">
                    <div className="sm:col-start-2">
                        {typeof member.patentOfficeLink === "string" && member.patentOfficeLink !== ""
                            && <a
                                className="underline inline-flex gap-1"
                                target="_blank"
                                rel="noreferrer"
                                href={asUrl(member.patentOfficeLink)}
                            >
                                <IconExternalLink />{t('external-link')}
                            </a>
                        }
                    </div>

                    <DocumentInfo title={t("application")} number={member.applicationNumber} date={member.applicationDate} />
                    <DocumentInfo title={t("publication")} number={member.publicationNumber} date={member.publicationDate} />
                    <DocumentInfo title={t("patent")} number={member.patentNumber} date={member.patentDate} />
                    <DocumentInfo title={t("unitaryPatentRegistrationDate")} number={member.unitaryPatentRegistrationDate} />
                    <DocumentInfo title={t("status")} number={statusDescription} />
                    <DocumentInfo title={t("expiryDate")} number={member.expiryDate} />
                    <DocumentInfo title={t("abandonmentDate")} number={member.abandonmentDate} />
                    <DocumentInfo title={t("ext-reference")} number={member.externalReference} />
                    <DocumentInfo title={t("num-claims")} number={member.numberClaims?.toString()} />

                    <div className="sm:col-span-2 flex flex-row flex-wrap items-start gap-1 empty:mt-0 mt-2">
                        {(["validated", "pctRouteFiling", "firstFiling", "unitaryPatent", "optOut"] as const)
                            .filter(v => member[v] === true)
                            .map(v =>
                                <div key={v}
                                    className="text-sm bg-pc-200 border border-pc-300 px-1 rounded-sm whitespace-nowrap text-pcx-700"
                                >{t(v)}</div>)
                        }
                    </div>
                </div>
            </div>
            <div className="pt-5 pb-3 flex flex-col gap-2 justify-between">
                <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-1">
                    <AgentKeyValues title={t("inventors")} agents={currentInventors} tagDisplays={tagDisplays} />
                    <AgentKeyValues title={t("applicants")} agents={currentApplicants} tagDisplays={tagDisplays} />
                    <AgentKeyValues title={t("owners")} agents={currentOwners} tagDisplays={tagDisplays} />
                </div>
                {isEditUser &&
                    <div className="self-end">
                        <Link to='/settings/agents' className="btn-secondary w-fit whitespace-nowrap text-sm">{t('manage-agents')}</Link>
                    </div>}
            </div>
        </div>
    )
}

function AgentKeyValues({ title, agents, tagDisplays }: { title: string; agents: number[]; tagDisplays: Record<number, string> }) {
    return <>
        <div className="sm:text-right text-gray-500">{title}</div>
        <div className="pl-4 sm:pl-0 flex flex-wrap gap-1">
            <span className="text-slate-500 hidden last:inline">-</span>
            {agents.map(a =>
                <span key={a} className="pr-2 inline-flex after:content-[';'] last:after:content-[''] last:pr-0">
                    <KanjiTip kanjiOpt={tagDisplays[a]}><span>{tagDisplays[a]}</span></KanjiTip>
                </span>
            )}
        </div>
    </>
}

interface DocumentInfoProps {
    title: string
    number?: string | ReactElement
    date?: string
}
function DocumentInfo({ title, number, date }: DocumentInfoProps) {
    if (number || date)
        return <>
            <div className="sm:text-right text-gray-500">{title}</div>
            <div className="pl-4 sm:pl-0">{number ?? '-'} {date && `(${date})`}</div>
        </>
    else
        return null
}

function MemberComments({ member }: { member: CompetitorMember }) {
    const { t } = useTranslation()
    const { commentsLookUp } = useComments()
    const { isEditUser } = useRoles()

    return (
        <div>
            <div className="pb-4 flex flex-row">
                <h4 className="grow">{t('comments')}</h4>
                {isEditUser &&
                    <Link
                        to={`comments?entity=${competitor_member}&entityId=${member.competitorMemberId}`}
                        className="btn-secondary py-0.5 text-sm whitespace-nowrap"
                        title={t('add-comment')}
                    >
                        {t('add')}
                    </Link>}
            </div>
            <div className="max-h-[20rem] xl:max-h-[36rem] overflow-y-auto flex flex-col gap-2">
                {(commentsLookUp[competitor_member]?.[member.competitorMemberId] ?? [])
                    .map(c => <Comment key={`comment-${c.commentId}`} {...c} />)
                }
            </div>
        </div>
    )
}

export function MemberDocuments() {
    const ctx = useCompetitorMember()

    if (!ctx) return null
    const { member, family } = ctx

    return <FocusedBrowser entity={competitor_member} internalReference={member.internalReference} parent={family.internalReference} parentName={family.familyName} />
}

export function CompetitorMemberCosts() {
    const ctx = useCompetitorMember()

    if (!ctx) return null
    const { member } = ctx

    return <>
        <ExternalMemberCosts competitorMemberId={member.competitorMemberId} />
        <Outlet />
    </>
}
